import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Tooltip } from "@mui/material";
import { ExternalConnectionModel, RequestResult, UpsertExternalConnectionInput } from "../../gql-types.generated";
import { useTitle } from "../../util/Common";
import { ToastSeverity, Viewer } from "../../util/Constants";
import { viewerCanEdit, viewerCanAddDelete } from "../../util/ViewerUtility";
import { setToastConfig } from "../EDIContainer/EDIContainerSlice";
import { fetchExternalConnectionList, upsertExternalConnection, deleteExternalConnection } from "./ExternalConnectionActions";
import ErrorMessage from '../../components/ErrorMessage';
import { selectExternalConnectionList, captureUpsertExternalConnectionStatus, captureDeleteExternalConnectionStatus, clearError, clearState, selectExternalConnectionError, selectUpsertExternalConnectionStatus, selectDeleteExternalConnectionStatus } from "./ExternalConnectionSlice";
import { MainContentBox, MainDataGridNoRowHover, DataGridListScrollBox, PageTitleToolbarGrid } from '../../util/SharedStyles';
import PageTitleBar from '../../components/PageTitleBar';
import CreateNewButton from '../../components/buttons/CreateNewButton';
import FiltersButton from '../../components/buttons/FiltersButton';
import { GridActionsCellItem, GridColumns, GridRowModel, GridOverlay, GridSortModel, GridRowParams, useGridApiContext, GridRenderCellParams } from '@mui/x-data-grid-pro';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import NoRecordsMessage from '../../components/NoRecordsMessage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExternalConnectionDialog from "../../components/dialogs/ExternalConnectionDialog";
import DeleteDialog from "../../components/dialogs/DeleteDialog";
import ExternalConnectionsFilterBar from "../../components/filters/ExternalConnectionsFilterBar";

interface ExternalConnectionsProps {
    viewer: Viewer | undefined;
}

const ExternalConnections: React.FC<ExternalConnectionsProps> = (props) => {
    const { viewer } = props;
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [openModify, setOpenModify] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [contentAreaHeight, setContentAreaHeight] = useState('auto');
    const [connectionRows, setConnectionRows] = useState<GridRowModel[] | undefined>(undefined);
    const [selectedConnection, setSelectedConnection] = useState<ExternalConnectionModel | undefined>(undefined);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'externalConnectionType',
            sort: 'asc',
        },
    ]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterIsForClient, setFilterIsForClient] = useState<boolean | undefined>(undefined);
    const [filterIsForPartner, setFilterIsForPartner] = useState<boolean | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);

    const connectionList = useAppSelector(selectExternalConnectionList);
    const upsertConnectionStatus = useAppSelector(selectUpsertExternalConnectionStatus);
    const deleteConnectionStatus = useAppSelector(selectDeleteExternalConnectionStatus);
    const error = useAppSelector(selectExternalConnectionError);
    const canEdit = viewerCanEdit(viewer);
    const canAddOrDelete = viewerCanAddDelete(viewer);

    useTitle("External Connections");

    useEffect(() => {
        // Load initial data when component mounts
        dispatch(fetchExternalConnectionList());
        
        return () => {
            dispatch(clearState());
        }
    }, []);

    useEffect(() => {
        const listHeaderHeight = document.getElementById('external-connections-title-comp')?.clientHeight || 0;
        // if filters are open, include that in the calculation
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('external-connections-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = listHeaderHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0) {
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
        }
    }, [connectionList, filtersOpen]);

    useEffect(() => {
        setConnectionRows(getConnectionRows());
        if (!connectionList) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [connectionList]);

    useEffect(() => {
        if (upsertConnectionStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: upsertConnectionStatus.message as string,
                severity: ToastSeverity.Success
            }));
            dispatch(captureUpsertExternalConnectionStatus());
            onConnectionDialogClose();
        }
    }, [upsertConnectionStatus?.result])

    useEffect(() => {
        if (deleteConnectionStatus?.result === RequestResult.Success) {
            onDeleteDialogClose();
            dispatch(setToastConfig({
                message: deleteConnectionStatus.message as string,
                severity: ToastSeverity.Success
            }));
        } else if (deleteConnectionStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteConnectionStatus.message as string);
        }
    }, [deleteConnectionStatus?.result])

    useEffect(() => {
        // keep track of the number of filters to know if should display badge in Filters button
        if (connectionList) {
            let numFilters = 0;
            if (filterIsForClient === true) {
                numFilters += 1;
            }
            if (filterIsForPartner === true) {
                numFilters += 1;
            }
            setFilterCount(numFilters);
        }
    }, [connectionList, filterIsForClient, filterIsForPartner]);

    const onAddConnectionClick = () => {
        dispatch(clearError());
        if (selectedConnection) {
            setSelectedConnection(undefined);
        }
        setOpenModify(true);
    }

    const onConnectionDialogClose = () => {
        setOpenModify(false);
        onDialogClose();
    };

    const onDeleteDialogClose = () => {
        setOpenDelete(false);
        onDialogClose();
        dispatch(captureDeleteExternalConnectionStatus());
        setDeleteErrorMessage('');
    };

    const onDialogClose = () => {
        dispatch(clearError());
        setSelectedConnection(undefined);
        // Refresh list to bring in potential updates with current filters
        dispatch(fetchExternalConnectionList({
            isForClient: filterIsForClient,
            isForPartner: filterIsForPartner
        }));
    };

    const onConnectionDialogSave = (connectionDetails: UpsertExternalConnectionInput) => {
        dispatch(upsertExternalConnection(connectionDetails));
    }

    const onDeleteDialogConfirm = (id: string) => {
        dispatch(deleteExternalConnection(id));
    }

    const getSelectedRowConnection = useCallback((rowId: string) => () => {
        if (rowId && connectionList?.length) {
            let connection = connectionList.find(connection => connection.externalConnectionType === rowId);
            return connection;
        }
        return undefined;
    }, [connectionList]);

    const editConnectionHandler = useCallback((rowId: string) => () => {
        let connection = getSelectedRowConnection(rowId)();
        if (connection) {
            setSelectedConnection(connection);
            setOpenModify(true);
        }
    }, [getSelectedRowConnection]);

    const deleteConnectionHandler = useCallback((rowId: string) => () => {
        let connection = getSelectedRowConnection(rowId)();
        if (connection) {
            setSelectedConnection(connection);
            setOpenDelete(true);
        }
    }, [getSelectedRowConnection]);

    const getConnectionRows = () => {
        if (connectionList && connectionList.length > 0) {
            return connectionList.map((connection) => {
                const { externalConnectionType, description, isForClient, isForPartner } = connection;

                return {
                    _raw: connection,
                    id: externalConnectionType,
                    externalConnectionType,
                    description,
                    isForClient,
                    isForPartner
                }

            }) as GridRowModel[];
        }
    };

    const connectionColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'TYPE',
                field: 'externalConnectionType',
                flex: 1,
                sortable: true,
                cellClassName: "ediDataGridCellFirstChild ediDataGridWrapCellContent-alwaysLeft"
            },
            {
                headerName: 'DESCRIPTION',
                field: 'description',
                flex: 2,
                sortable: true,
                cellClassName: "ediDataGridWrapCellContent-alwaysLeft"
            },
            {
                headerName: 'FOR CLIENT',
                field: 'isForClient',
                flex: 1,
                align: 'left',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    if (value) {
                        return <div style={{ width: '80px' }}><CheckCircleIcon color="success" /></div>
                    }
                    return (<div></div>);
                }
            },
            {
                headerName: 'FOR PARTNER',
                field: 'isForPartner',
                flex: 1,
                align: 'left',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    if (value) {
                        return <div style={{ width: '80px' }}><CheckCircleIcon color="success" /></div>
                    }
                    return (<div></div>);
                }
            },
            {
                field: 'actions',
                type: 'actions',
                sortable: false,
                resizable: false,
                minWidth: 100,
                headerAlign: 'center',
                align: 'right',
                hide: !canEdit, // hide column for reader role
                cellClassName: "ediDataGridCellLastChild",
                // eslint-disable-next-line react/display-name
                getActions: (params: GridRowParams<GridRowModel>) => [
                    <GridActionsCellItem
                        icon={<Tooltip title="Edit External Connection"><EditIcon /></Tooltip>}
                        label="Edit"
                        color="primary"
                        onClick={editConnectionHandler(params.row.id)}
                        showInMenu={false}
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title="Delete External Connection"><DeleteIcon /></Tooltip>}
                        label="Delete"
                        color="error"
                        hidden={!canAddOrDelete}
                        onClick={deleteConnectionHandler(params.row.id)}
                        showInMenu={false}
                    />
                ],
            },
        ], [canEdit, canAddOrDelete, editConnectionHandler, deleteConnectionHandler]
    )

    const GetApiRef = () => {
        return useGridApiContext();
    }
    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the External Connections' error={error}></ErrorMessage>
                )}
                {!error && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the External Connections' error={error}></ErrorMessage>
                )}
                {!error && (connectionList && connectionList.length <= 0) && (
                    <NoRecordsMessage topMargin={6} message="" />
                )}
            </GridOverlay>
        );
    };

    const onSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    const onFiltersClick = () => {
        // Show/hide filters bar
        setFiltersOpen(!filtersOpen);
        // reset filters cleared to false since not clicking close button on bar at this point
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        // set filters as cleared
        setFiltersCleared(true);
        setFilterCount(0);
        // hide filter bar
        setFiltersOpen(false);
    };

    const refreshFilters = (filterIsForClientParam: boolean | undefined, filterIsForPartnerParam: boolean | undefined) => {
        setFilterIsForClient(filterIsForClientParam);
        setFilterIsForPartner(filterIsForPartnerParam);
        
        // clearing state to show loading overlay
        dispatch(clearState());
        // filters changed so refresh the list
        dispatch(fetchExternalConnectionList({
            isForClient: filterIsForClientParam,
            isForPartner: filterIsForPartnerParam
        }));
    };

    return (
        <MainContentBox>
            <PageTitleBar text='External Connection Types' id="external-connections-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="external-connection-filters"
                    />
                    {canAddOrDelete &&
                        <CreateNewButton
                            text="New Connection Type"
                            onClick={onAddConnectionClick}
                            data-cy="add-new-external-connection"
                        />}
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <ExternalConnectionsFilterBar
                id="external-connections-filter-bar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGridNoRowHover
                    loading={isLoading || connectionList === undefined}
                    headerHeight={38}
                    rowHeight={52}
                    aria-label="External Connection List"
                    hideFooter
                    disableColumnMenu
                    disableColumnFilter
                    disableSelectionOnClick
                    rows={connectionRows ?? []}
                    columns={connectionColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                    }}
                />

            </DataGridListScrollBox>
            <ExternalConnectionDialog
                isOpen={openModify}
                externalConnection={selectedConnection}
                onClose={onConnectionDialogClose}
                onSave={onConnectionDialogSave}
                error={error}
            />
            <DeleteDialog
                isOpen={openDelete}
                id={selectedConnection?.externalConnectionType ?? ''}
                heading={'Delete External Connection'}
                message={`Deletion of this connection type will also delete it from any associated Clients and/or Partners.\n\nAre you sure you want to delete '${selectedConnection?.externalConnectionType}'?`}
                onConfirm={onDeleteDialogConfirm}
                onReject={onDeleteDialogClose}
                errorMessage={deleteErrorMessage}
                maxWidth='xs'
            />
        </MainContentBox>
    )
}

export default ExternalConnections;
